/**
 * @class YouTubePlayer wraps the YouTube API 'YT.Player' class to set defaults and automatically load the API file.
 */
class YouTubePlayer { // eslint-disable-line no-unused-vars, no-redeclare
    /** @public @member {YT.Player} player - stores the YouTube Player object that is being wrapped */
    player;

    /** 
     * @public @member   {Object} events                      - an object containing arrays of event listeners 
     * @public @property {SimplePubSub}  events.onReady       - allows subscribing event listeners for the YT.Player.onReady event
     * @public @property {SimplePubSub}  events.onStateChange - allows subscribing event listeners for the YT.Player.onStateChange event
     * @public @property {SimplePubSub}  events.onError       - allows subscribing event listeners for the YT.Player.onError event
    */
    
    events = {
        onReady: new SimplePubSub(),
        onStateChange: new SimplePubSub(),
        onError: new SimplePubSub()
    };

    /**
     * Constructs an instance of the YouTubePlayer object
     * @param {string}   iframeOrDivId                  - The id attr value of the div or iframe to contain the player 
     * @param {object}   ParamsObj                      - The second argument containing all remaining parameters as properties
     * @param {number}   ParamsObj.height               - The height of the player (required unless attaching to an iframe where this is already set)
     * @param {number}   ParamsObj.width                - The width of the player  (required unless attaching to an iframe where this is already set)
     * @param {string}   ParamsObj.videoId              - The YouTube video code
     * @param {object}   ParamsObj.playerVars           - Can contain any of the properties listed here: https://developers.google.com/youtube/player_parameters
     * @param {object}   ParamsObj.events               - Object containing callbacks for events that should be listened to
     * @param {function} ParamsObj.events.onReady       - callback for YouTube Player event 'onReady'
     * @param {function} ParamsObj.events.onStateChange - callback for YouTube Player event 'onStateChange'
     */
    constructor(iframeOrDivId, { height, width, videoId, playerVars, events: { onError, onReady, onStateChange } = {}}) {
        if ((typeof onReady === "function"))       this.events.onReady.subscribe("PlayerConstructor", onReady);
        if ((typeof onStateChange === "function")) this.events.onStateChange.subscribe("PlayerConstructor", onStateChange);
        if ((typeof onError === "function"))       this.events.onError.subscribe("PlayerConstructor", onError);

        var title = document.querySelector("#" + iframeOrDivId)?.title;

        playerVars = playerVars != null && (typeof playerVars === "object") ? playerVars : {};

        var paramsObj = {
            origin: window.location.origin,
            playerVars,
            events: {
                onReady: (event) => this.events.onReady.publish(event),
                onStateChange: (event) => this.events.onStateChange.publish(event, event.data),
                onError: (event) => this.events.onError.publish(event)
            }
        };

        if (videoId) paramsObj.videoId = videoId;
        if (width)   paramsObj.width   = width;
        if (height)  paramsObj.height  = height;

        this.events.onError.subscribe("DefaultLogging", (event) => {
            switch (event.data) {
                case 2:   console.error("Invalid Parameter value");  break;
                case 5:   console.error("HTML5 player error.");      break;
                case 100: console.error("Video not found");          break;
                case 101: console.error("Video cannot be embedded"); break;
                case 150: console.error("Video cannot be embedded"); break;
                // no default
            }
        });

        // Assign values to playerVars that should always be set with these values.
        Object.assign(paramsObj.playerVars, {
            hl: AppState.GetCurrentLanguageName(),
            modestbranding: 1,
            rel: 0
        });

        console.table({ iframeOrDivId, height, width, videoId, playerVars, onError, onReady, onStateChange });

        // Wait for API to load then create the player.
        var initPlayerPromise = YouTubeApiLoader.Load().then(() => {
            return new Promise(accept => {
                var playerAlreadyExists = false;
                this.events.onReady.subscribe("initPlayer", () => {
                    accept(playerAlreadyExists);
                });
                var player = window.YT.get(iframeOrDivId);
                if (player) {
                    console.debug("Player already exists");
                    playerAlreadyExists = true;
                    player.addEventListener('onStateChange', (event) => this.events.onStateChange.publish(event, event.data));
                    player.addEventListener('onError', (event) => this.events.onError.publish(event));
                }
                else {
                    console.debug("Creating a new YT.Player");
                    player = new window.YT.Player(iframeOrDivId, paramsObj);
                }
                this.player = player;

                if (playerAlreadyExists) {
                    this.events.onReady.publish();
                }
            });
        });

        this.WaitResolvePlayer = initPlayerPromise.then((playerAlreadyExists) => {
            console.debug("YouTubePlayer.js: Player Ready");
            if (title) {
                var iframe = this.player.getIframe();
                if (iframe) {
                    iframe.title = title;
                }
            }
            if (playerAlreadyExists) {
                if ((typeof this.player.getVideoData === "function")) {
                    console.debug("Getting data for existing player");
                    var data = this.player.getVideoData();
                    if (data.video_id && videoId && data.video_id !== videoId) {
                        console.debug("Setting id for existing player");
                        if (playerVars.autoplay) {
                            this.loadVideoById(videoId);
                        } else {
                            this.cueVideoById(videoId);
                        }
                    }
                }
                else if (videoId) {
                    console.debug("Setting id for existing player");
                    if (playerVars.autoplay) {
                        this.loadVideoById(videoId);
                    }
                    else {
                        this.cueVideoById(videoId);
                    }
                }
            }
        });
    }

    playVideo() {
        if (!this.player || (typeof this.player?.playVideo === "function") === false) {
            console.debug("YouTubePlayer.js: Waiting for player");
            this.WaitResolvePlayer.then(() => this.playVideo());
        }
        else {
            console.debug("YouTubePlayer.js: Playing Video");
            this.player.playVideo();
        }
        return this;
    }

    cueVideoById(youTubeId) {
        if (!this.player || (typeof this.player?.cueVideoById === "function") === false) {
            console.debug("YouTubePlayer.js: Waiting for player");
            this.WaitResolvePlayer.then(() => this.cueVideoById(youTubeId));
        }
        else {
            console.debug("YouTubePlayer.js: Cue Video");
            this.player.cueVideoById(youTubeId);
        }
        return this;
    }

    loadVideoById(youTubeId) {
        if (!this.player || (typeof this.player?.loadVideoById === "function") === false) {
            console.debug("YouTubePlayer.js: Waiting for player");
            this.WaitResolvePlayer.then(() => this.loadVideoById(youTubeId));
        }
        else {
            console.debug("YouTubePlayer.js: Playing Video");
            this.player.loadVideoById(youTubeId);
        }
        return this;
    }
}

window.YouTubePlayer = YouTubePlayer;